import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { PieChart, Pie, Cell, Tooltip, ResponsiveContainer } from 'recharts';
import { Settings, Globe } from 'lucide-react';
import { useComponentMetrics, getMetricObjectiveClassName, libelleTauxConversion, libelleConversions } from '../utils/metricsUtils';
import MetricsConfigModal from './MetricsConfigModal';
import ModuleVisibilityToggle from './ModuleVisibilityToggle';

import { calculateVariation, makeRenderVariation } from '../utils/variationUtils';
import { bornerLegende } from '../utils/legendeUtils';
/**
 * GA4BrowsersChart - Composant pour afficher les métriques par navigateur GA4
 * Navigateurs: Chrome, Safari, Firefox, Edge, etc.
 */
const GA4BrowsersChart = ({
  browsers = [],
  previousBrowsers = [],
  compareEnabled = false,
  formatNumber,
  loading = false,
  conversionLabel = 'Conversions',
  serverTotals = null,
  serverPreviousTotals = null,
  // Props pour la configuration des métriques
  accountId = 'global',
  userRole = 'USER',
  isClientModuleVisible,
  toggleClientModuleVisibility,
  moduleId = 'ga4_browsers',
  // Props pour les options avancées (conversions)
  token = null,
  propertyId = null,
  conversionConfig = null,
  onConversionConfigSave = null
}) => {
  const [isVisible, setIsVisible] = useState(true);
  const [sortConfig, setSortConfig] = useState({ key: 'sessions', direction: 'desc' });
  const [showMetricsConfig, setShowMetricsConfig] = useState(false);
  /*
   * Dix lignes à l'écran, le reste à la page suivante — comme les Canaux et la Géographie, qui
   * paginaient déjà. Ce tableau affichait tout, et la traîne occupait plusieurs écrans.
   */
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  // Hook pour les métriques du composant (réutilise ga4_devices pour l'instant)
  const componentMetrics = useComponentMetrics('ga4_devices', accountId);

  const isMetricActive = useCallback((metricKey) => {
    return componentMetrics.metrics.includes(metricKey);
  }, [componentMetrics.metrics]);

  const metricsOrder = componentMetrics.order || componentMetrics.metrics || [];

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7300', '#00C49F', '#FFBB28'];


  const renderVariation = makeRenderVariation({ fontSize: '11px', format: (v) => formatNumber(v, 1) });

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const sortedBrowsers = useMemo(() => {
    if (!browsers || !browsers.length) return [];

    const sorted = [...browsers];
    sorted.sort((a, b) => {
      let aValue, bValue;

      if (sortConfig.key === 'browser') {
        aValue = a.browser || '';
        bValue = b.browser || '';
        return sortConfig.direction === 'asc'
          ? String(aValue).localeCompare(String(bValue))
          : String(bValue).localeCompare(String(aValue));
      }

      if (sortConfig.key === 'conversionRate') {
        aValue = a.sessions > 0 ? (a.conversions / a.sessions * 100) : 0;
        bValue = b.sessions > 0 ? (b.conversions / b.sessions * 100) : 0;
      } else {
        aValue = Number(a[sortConfig.key]) || 0;
        bValue = Number(b[sortConfig.key]) || 0;
      }

      return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
    });
    return sorted;
  }, [browsers, sortConfig]);

  const totalPages = Math.ceil(sortedBrowsers.length / ITEMS_PER_PAGE);
  const lignesAffichees = useMemo(() => {
    const debut = (currentPage - 1) * ITEMS_PER_PAGE;
    return sortedBrowsers.slice(debut, debut + ITEMS_PER_PAGE);
  }, [sortedBrowsers, currentPage]);

  // Changer de tri remet au début : rester en page 3 d'un autre classement n'a pas de sens.
  useEffect(() => { setCurrentPage(1); }, [sortConfig, browsers]);

  const totals = useMemo(() => {
    // Le total d'une période ne dépend pas de la dimension : on affiche celui que GA4 a
    // calculé pour l'ensemble du site, jamais une addition des lignes reçues.
    if (serverTotals && serverTotals.sessions > 0) return serverTotals;

    if (!browsers || !browsers.length) return { sessions: 0, users: 0, pageViews: 0, bounceRate: 0, avgSessionDuration: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 };

    const result = browsers.reduce((acc, browser) => ({
      sessions: acc.sessions + (browser.sessions || 0),
      users: acc.users + (browser.users || 0),
      pageViews: acc.pageViews + (browser.pageViews || 0),
      bounceSum: acc.bounceSum + ((browser.bounceRate || 0) * (browser.sessions || 0)),
      durationSum: acc.durationSum + ((browser.avgSessionDuration || 0) * (browser.sessions || 0)),
      conversions: acc.conversions + (browser.conversions || 0),
      conversionsValue: acc.conversionsValue + (browser.conversionsValue || 0),
      transactions: acc.transactions + (browser.transactions || 0),
      totalRevenue: acc.totalRevenue + (browser.totalRevenue || 0)
    }), { sessions: 0, users: 0, pageViews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [browsers, serverTotals]);

  const previousTotals = useMemo(() => {
    if (!compareEnabled || !previousBrowsers || !previousBrowsers.length) return null;

    // Même règle que pour le total courant : la période de comparaison a elle aussi
    // son total calculé par GA4, sinon la variation compare deux choses différentes.
    if (serverPreviousTotals && serverPreviousTotals.sessions > 0) return serverPreviousTotals;

    const result = previousBrowsers.reduce((acc, browser) => ({
      sessions: acc.sessions + (browser.sessions || 0),
      users: acc.users + (browser.users || 0),
      pageViews: acc.pageViews + (browser.pageViews || 0),
      bounceSum: acc.bounceSum + ((browser.bounceRate || 0) * (browser.sessions || 0)),
      durationSum: acc.durationSum + ((browser.avgSessionDuration || 0) * (browser.sessions || 0)),
      conversions: acc.conversions + (browser.conversions || 0),
      conversionsValue: acc.conversionsValue + (browser.conversionsValue || 0),
      transactions: acc.transactions + (browser.transactions || 0),
      totalRevenue: acc.totalRevenue + (browser.totalRevenue || 0)
    }), { sessions: 0, users: 0, pageViews: 0, bounceSum: 0, durationSum: 0, conversions: 0, conversionsValue: 0, transactions: 0, totalRevenue: 0 });

    result.bounceRate = result.sessions > 0 ? result.bounceSum / result.sessions : 0;
    result.avgSessionDuration = result.sessions > 0 ? result.durationSum / result.sessions : 0;
    result.transactionRate = result.sessions > 0 ? (result.transactions / result.sessions * 100) : 0;

    return result;
  }, [compareEnabled, previousBrowsers, serverPreviousTotals]);

  // Données pour le pie chart Sessions
  const sessionsChartData = useMemo(() => {
    if (!browsers || !browsers.length) return [];

    return browsers.map(browser => ({
      name: browser.browser,
      value: browser.sessions || 0,
      percentage: totals.sessions > 0 ? ((browser.sessions / totals.sessions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [browsers, totals.sessions]);

  // Données pour le pie chart Conversions
  const conversionsChartData = useMemo(() => {
    if (!browsers || !browsers.length) return [];

    return browsers.map(browser => ({
      name: browser.browser,
      value: browser.conversions || 0,
      percentage: totals.conversions > 0 ? ((browser.conversions / totals.conversions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [browsers, totals.conversions]);

  // Données pour le pie chart Transactions (e-commerce)
  const transactionsChartData = useMemo(() => {
    if (!browsers || !browsers.length) return [];

    return browsers.map(browser => ({
      name: browser.browser,
      value: browser.transactions || 0,
      percentage: totals.transactions > 0 ? ((browser.transactions / totals.transactions) * 100).toFixed(1) : 0
    })).filter(item => parseFloat(item.percentage) > 0);
  }, [browsers, totals.transactions]);

  // Détermine si on affiche Transactions ou Conversions dans le pie chart
  const showTransactionsPieChart = isMetricActive('transactions') && !isMetricActive('conv');
  const pieChartData = showTransactionsPieChart ? transactionsChartData : conversionsChartData;
  const pieChartLabel = showTransactionsPieChart
    ? 'Transactions'
    : libelleConversions(conversionConfig, conversionLabel);
  const pieChartTotal = showTransactionsPieChart ? totals.transactions : totals.conversions;

  // Mapping navigateur → couleur
  const browserColorMap = useMemo(() => {
    const map = {};
    browsers.forEach((browser, index) => {
      map[browser.browser] = COLORS[index % COLORS.length];
    });
    return map;
  }, [browsers]);

  const legendData = useMemo(() => {
    if (!browsers || !browsers.length) return [];

    return browsers.map((browser, index) => ({
      value: browser.browser,
      color: COLORS[index % COLORS.length]
    }));
  }, [browsers]);

  // Dix étiquettes au plus : au-delà, la colonne s'étire sur plusieurs écrans et désigne des
  // parts invisibles. Le camembert et les totaux, eux, portent toujours sur tout.
  const { visibles: legendeVisible, reste: legendeReste } = bornerLegende(legendData);

  const columnConfig = useMemo(() => ({
    visites: {
      key: 'sessions',
      label: 'Sessions',
      getValue: (item) => item.sessions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.sessions || 0
    },
    visiteurs: {
      key: 'users',
      label: 'Utilisateurs',
      getValue: (item) => item.users || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.users || 0
    },
    rebond: {
      key: 'bounceRate',
      label: '% de Rebond',
      getValue: (item) => item.bounceRate || 0,
      format: (val) => `${formatNumber(val, 1)}%`,
      getPrevValue: (prev) => prev?.bounceRate || 0,
      inverted: true
    },
    pages_vues: {
      key: 'pageViews',
      label: 'Pages vues',
      getValue: (item) => item.pageViews || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.pageViews || 0
    },
    taux_conv: {
      key: 'conversionRate',
      label: libelleTauxConversion(conversionConfig, conversionLabel),
      getValue: (item) => item.sessions > 0 ? (item.conversions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.conversions / prev.sessions * 100) : 0
    },
    conv: {
      key: 'conversions',
      label: libelleConversions(conversionConfig, conversionLabel),
      getValue: (item) => item.conversions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.conversions || 0
    },
    valeur_conv: {
      key: 'conversionsValue',
      label: 'Valeur Conv.',
      getValue: (item) => item.conversionsValue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.conversionsValue || 0
    },
    transactions: {
      key: 'transactions',
      label: 'Transactions',
      getValue: (item) => item.transactions || 0,
      format: (val) => formatNumber(val),
      getPrevValue: (prev) => prev?.transactions || 0
    },
    chiffre_affaires: {
      key: 'totalRevenue',
      label: 'Chiffre d\'affaires',
      getValue: (item) => item.totalRevenue || 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev?.totalRevenue || 0
    },
    taux_transaction: {
      key: 'transactionRate',
      label: '% Conversion (transactions)',
      getValue: (item) => item.sessions > 0 ? (item.transactions / item.sessions * 100) : 0,
      format: (val) => `${formatNumber(val, 2)}%`,
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.transactions / prev.sessions * 100) : 0
    },
    panier_moyen: {
      key: 'avgOrderValue',
      label: 'Panier moyen',
      getValue: (item) => item.transactions > 0 ? (item.totalRevenue || 0) / item.transactions : 0,
      format: (val) => `${formatNumber(val, 2)}€`,
      getPrevValue: (prev) => prev && prev.transactions > 0 ? (prev.totalRevenue || 0) / prev.transactions : 0
    },
    duree_moy: {
      key: 'avgSessionDuration',
      label: 'Durée moyenne',
      getValue: (item) => item.avgSessionDuration || 0,
      format: (val) => {
        const mins = Math.floor(val / 60);
        const secs = Math.floor(val % 60);
        return `${mins}:${secs.toString().padStart(2, '0')}`;
      },
      getPrevValue: (prev) => prev?.avgSessionDuration || 0
    },
    pages_visite: {
      key: 'pagesPerSession',
      label: 'Pages/Session',
      getValue: (item) => item.sessions > 0 ? (item.pageViews || 0) / item.sessions : 0,
      format: (val) => formatNumber(val, 1),
      getPrevValue: (prev) => prev && prev.sessions > 0 ? (prev.pageViews || 0) / prev.sessions : 0
    }
  }), [formatNumber, conversionLabel, conversionConfig]);

  if (loading) {
    return (
      <div className="campaigns-table-section">
        <div className="section-header">
          <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Globe size={18} /> Navigateurs</h4>
        </div>
        <div className="table-container">
          <div className="loading-message" style={{ textAlign: 'center', padding: '40px' }}>
            <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
            Chargement des navigateurs...
          </div>
        </div>
      </div>
    );
  }

  if (!browsers || browsers.length === 0) {
    return null;
  }

  return (
    <>
      <div className="section-header">
        <h4 style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Globe size={18} /> Navigateurs</h4>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {userRole !== 'CLIENT' && (
            <button
              onClick={() => setShowMetricsConfig(true)}
              className="metrics-settings-btn"
              title="Configurer les métriques"
            >
              <Settings size={16} />
            </button>
          )}
          {toggleClientModuleVisibility ? (
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible ? isClientModuleVisible(moduleId) : true}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId={moduleId}
              title="Navigateurs GA4"
            />
          ) : (
            <button
              onClick={() => setIsVisible(!isVisible)}
              className="section-toggle-btn"
              title={isVisible ? "Masquer la section" : "Afficher la section"}
            >
              {isVisible ? '−' : '+'}
            </button>
          )}
        </div>
      </div>

      {isVisible && (
        <>
          {/* Graphiques en secteurs */}
          {sessionsChartData.length > 0 && (
            <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {/* Pie chart Sessions (gauche) */}
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Sessions par navigateur</h5>
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={sessionsChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ percentage }) => `${percentage}%`}
                        outerRadius={70}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {sessionsChartData.map((entry, index) => (
                          <Cell key={`sessions-cell-${index}`} fill={browserColorMap[entry.name] || COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value, name) => [formatNumber(value) + ' sessions', name]} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>

                {/* Légende (centre) */}
                <div style={{
                  minWidth: '150px',
                  padding: '0 20px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center'
                }}>
                  {legendeVisible.map((item, index) => (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: '8px',
                      fontSize: '13px'
                    }}>
                      <div style={{
                        width: '12px',
                        height: '12px',
                        backgroundColor: item.color,
                        marginRight: '8px',
                        borderRadius: '2px'
                      }}></div>
                      <span>{item.value}</span>
                    </div>
                  ))}
                  {/* Une liste qui s'arrête en silence se lit comme une liste complète. */}
                  {legendeReste > 0 && (
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px' }}>
                      + {legendeReste} autres
                    </div>
                  )}
                </div>

                {/* Pie chart Conversions/Transactions (droite) */}
                {pieChartData.length > 0 && pieChartTotal > 0 && (
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>{pieChartLabel} par navigateur</h5>
                    <ResponsiveContainer width="100%" height={280}>
                      <PieChart>
                        <Pie
                          data={pieChartData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ percentage }) => `${percentage}%`}
                          outerRadius={70}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieChartData.map((entry, index) => (
                            <Cell key={`pie-cell-${index}`} fill={browserColorMap[entry.name] || COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [formatNumber(value) + ` ${pieChartLabel.toLowerCase()}`, name]} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tableau détaillé */}
          <div className="device-table-wrapper" style={{ marginTop: '0', paddingTop: '0' }}>
            <table className="devices-table" style={{ width: '100%', tableLayout: 'auto', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th onClick={() => handleSort('browser')} className="sortable" style={{ cursor: 'pointer' }}>
                    Navigateur {sortConfig.key === 'browser' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                  </th>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    return (
                      <th key={metricKey} onClick={() => handleSort(col.key)} className="sortable numeric" style={{ cursor: 'pointer' }}>
                        <span className="th-libelle">{col.label}</span> {sortConfig.key === col.key && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                      </th>
                    );
                  })}
                </tr>
              </thead>

              <tbody>
                {lignesAffichees.map((browser, index) => {
                  const prev = compareEnabled && previousBrowsers
                    ? previousBrowsers.find(p => p.browser === browser.browser)
                    : null;

                  return (
                    <tr key={browser.browser || index}>
                      <td className="device-name" style={{ whiteSpace: 'nowrap' }}>
                        <span style={{
                          display: 'inline-block',
                          width: '10px',
                          height: '10px',
                          borderRadius: '50%',
                          backgroundColor: browserColorMap[browser.browser] || COLORS[index % COLORS.length],
                          marginRight: '8px',
                          flexShrink: 0
                        }}></span>
                        {browser.browser}
                      </td>
                      {metricsOrder.map(metricKey => {
                        if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                        const col = columnConfig[metricKey];
                        const value = col.getValue(browser);
                        const prevValue = prev ? col.getPrevValue(prev) : null;
                        const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_devices', accountId);
                        return (
                          <td key={metricKey} className={`numeric ${objectiveClass}`}>
                            {col.format(value)}
                            {prev && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>

              <tfoot>
                <tr className="total-row">
                  <td><strong>TOTAL</strong></td>
                  {metricsOrder.map(metricKey => {
                    if (!isMetricActive(metricKey) || !columnConfig[metricKey]) return null;
                    const col = columnConfig[metricKey];
                    const value = col.getValue(totals);
                    const prevValue = previousTotals ? col.getPrevValue(previousTotals) : null;
                    const objectiveClass = getMetricObjectiveClassName(metricKey, value, 'ga4_devices', accountId);
                    return (
                      <td key={metricKey} className={`numeric ${objectiveClass}`}>
                        <strong>{col.format(value)}</strong>
                        {previousTotals && renderVariation(calculateVariation(value, prevValue), col.inverted)}
                      </td>
                    );
                  })}
                </tr>
              </tfoot>
            </table>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-controls">
                <div className="pagination-info">
                  Affichage {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, sortedBrowsers.length)} sur {sortedBrowsers.length} navigateurs
                </div>
                <div className="pagination-buttons">
                  <button
                    onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
                    disabled={currentPage === 1}
                    className="pagination-btn"
                  >
                    Precedent
                  </button>
                  <button
                    onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
                    disabled={currentPage === totalPages}
                    className="pagination-btn"
                  >
                    Suivant
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal de configuration des métriques */}
      <MetricsConfigModal
        isOpen={showMetricsConfig}
        onClose={() => setShowMetricsConfig(false)}
        componentId="ga4_devices"
        accountId={accountId}
        token={token}
        propertyId={propertyId}
        currentAdvancedConfig={conversionConfig}
        onAdvancedConfigSave={onConversionConfigSave}
      />
    </>
  );
};

export default GA4BrowsersChart;
